import * as React from 'react';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import LockIcon from '@mui/icons-material/Lock';
import ScheduleIcon from '@mui/icons-material/Schedule';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import ViewDayIcon from '@mui/icons-material/ViewDay';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import { useSelector } from 'react-redux';
import { getJobs } from '../../../store/slices/jobsSlice';
import { getUser } from '../../../store/slices/userSlice';
import { getActiveDispatchers, getTechnicians } from '../../../store/slices/usersSlice';
import JobDetailsDialog from '../jobs/JobDetailsDialog';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { JOB_STATUSES } from '../../../utils/jobStatuses';
import { getPriorityLabel } from '../../../utils/jobPriorities';
import { isTechnicianAssignable } from '../../../utils/technicianStatus';
import useIsMobile from '../../../hooks/useIsMobile';
import {
  buildQueueMetadata,
  ensureMinimumEventDuration,
  getDayKey,
  hasExplicitTime,
  JOB_EVENT_MIN_DURATION_MINUTES,
  VISIT_EVENT_MIN_DURATION_MINUTES,
} from './calendarQueueUtils';

// Create a moment localizer for react-big-calendar
const localizer = momentLocalizer(moment);

// Parse date string as local time (not UTC)
// This handles both "YYYY-MM-DD" and ISO format "YYYY-MM-DDTHH:mm:ss.sssZ"
const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  // If it's just a date (YYYY-MM-DD), parse as local time
  if (dateString.length === 10 && dateString.includes('-')) {
    const [year, month, day] = dateString.split('-');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }
  // Otherwise, use moment for consistent parsing
  return moment(dateString).toDate();
};

/** Calendar job/visit block colors by job status (not priority). */
const getCalendarEventStatusColors = (statusId) => {
  const id = Number(statusId);
  if (
    id === JOB_STATUSES.CANCELLED.id ||
    id === JOB_STATUSES.RECALL_NO_CHARGE.id ||
    id === JOB_STATUSES.INVOICED_LOSS.id
  ) {
    return { backgroundColor: '#fee2e2', borderColor: '#991b1b' };
  }
  if (
    id === JOB_STATUSES.INVOICED.id ||
    id === JOB_STATUSES.READY_FOR_INVOICING.id ||
    id === JOB_STATUSES.DONE.id
  ) {
    return { backgroundColor: '#d1fae5', borderColor: '#065f46' };
  }
  return { backgroundColor: '#dbeafe', borderColor: '#1e3a8a' };
};

// Priority styling: 1=EMG P4, 2=P7, 3=P24, 4=P48 (from jobPriorities)
const getPriorityStyle = (priority) => {
  const p = Number(priority);
  const label = getPriorityLabel(p);
  if (p === 1) return { label, bgColor: '#fee2e2', textColor: '#991b1b' };   // EMG P4 - red
  if (p === 2) return { label, bgColor: '#fef3c7', textColor: '#92400e' };   // P7 - amber
  if (p === 3) return { label, bgColor: '#dbeafe', textColor: '#1e40af' };   // P24 - blue
  if (p === 4) return { label, bgColor: '#e5e7eb', textColor: '#374151' };   // P48 - gray
  return { label, bgColor: '#fef3c7', textColor: '#92400e' }; // default P7
};

const getInitials = (name) => {
  const trimmed = (name || '').trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
};

const getVisitEventSurfaceStyle = (isDarkMode, statusBorderColor) => ({
  backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.16)' : '#eef2ff',
  borderColor: isDarkMode ? 'rgba(129, 140, 248, 0.55)' : '#c7d2fe',
  borderStyle: 'solid',
  borderWidth: '1px',
  borderLeft: `3px solid ${statusBorderColor}`,
  color: isDarkMode ? '#e0e7ff' : '#312e81',
  borderRadius: '6px',
  padding: 0,
  minHeight: '60px',
  boxShadow: isDarkMode
    ? 'inset 0 1px 0 rgba(255, 255, 255, 0.06)'
    : '0 1px 2px rgba(99, 102, 241, 0.12)',
});

const QueueMoreButton = ({ label, onClick }) => (
  <Button
    size="small"
    onClick={onClick}
    sx={{
      position: 'absolute',
      left: 2,
      right: 2,
      bottom: -18,
      zIndex: 3,
      minHeight: '18px',
      textTransform: 'none',
      fontSize: '0.65rem',
      lineHeight: 1.2,
      px: 0.75,
      py: 0,
      borderRadius: 1,
      backgroundColor: 'background.paper',
      color: 'text.primary',
      border: (theme) =>
        theme.palette.mode === 'dark' ? '1px solid #475569' : '1px solid #cbd5e1',
      boxShadow: (theme) =>
        theme.palette.mode === 'dark'
          ? '0 1px 4px rgba(0,0,0,0.35)'
          : '0 1px 4px rgba(15,23,42,0.12)',
      '&:hover': {
        backgroundColor: 'action.hover',
      },
    }}
  >
    {label}
  </Button>
);

const VisitEventContent = ({
  job,
  technicianName,
  visitTime,
  clientName,
  description,
  isDarkMode,
  isTimeGridView,
  queueMoreLabel,
  onQueueMoreClick,
}) => {
  const initials = getInitials(technicianName);
  const timeLabel = visitTime || 'All day';

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        width: '100%',
        minWidth: 0,
        overflow: isTimeGridView ? 'visible' : 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 0.75,
          p: isTimeGridView ? '4px 6px' : '5px 7px',
          height: '100%',
          minWidth: 0,
        }}
      >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: '0.625rem',
          fontWeight: 700,
          letterSpacing: '0.02em',
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(129, 140, 248, 0.45) 0%, rgba(99, 102, 241, 0.25) 100%)'
            : 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
          color: isDarkMode ? '#eef2ff' : '#3730a3',
          border: isDarkMode ? '1px solid rgba(165, 180, 252, 0.35)' : '1px solid #a5b4fc',
        }}
      >
        {initials}
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 0.5,
            minWidth: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.375, minWidth: 0, flex: 1 }}>
            {job?.is_locked && (
              <Tooltip title="Job locked - read only" arrow placement="top">
                <LockIcon sx={{ fontSize: 11, color: 'inherit', flexShrink: 0, opacity: 0.85 }} />
              </Tooltip>
            )}
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                fontSize: '0.72rem',
                lineHeight: 1.2,
                color: 'inherit',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {technicianName || 'Technician'}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.25,
              flexShrink: 0,
              px: 0.75,
              py: 0.125,
              borderRadius: '999px',
              fontSize: '0.6rem',
              fontWeight: 600,
              lineHeight: 1.2,
              backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.35)' : 'rgba(255, 255, 255, 0.75)',
              color: 'inherit',
              border: isDarkMode ? '1px solid rgba(165, 180, 252, 0.25)' : '1px solid rgba(99, 102, 241, 0.2)',
            }}
          >
            <ScheduleIcon sx={{ fontSize: 11, opacity: 0.9 }} />
            {timeLabel}
          </Box>
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.65rem',
            lineHeight: 1.2,
            color: 'inherit',
            opacity: 0.88,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {clientName}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.65rem',
            lineHeight: 1.25,
            color: 'inherit',
            opacity: 0.75,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: isTimeGridView ? 1 : 2,
            WebkitBoxOrient: 'vertical',
            wordBreak: 'break-word',
            flexShrink: 0,
          }}
        >
          {description}
        </Typography>
      </Box>
      </Box>
      {queueMoreLabel && (
        <QueueMoreButton
          label={queueMoreLabel}
          onClick={(e) => {
            e.stopPropagation();
            onQueueMoreClick(e);
          }}
        />
      )}
    </Box>
  );
};

// Custom Event Component
const EventComponent = ({ event, isDarkMode, isTimeGridView, queueMoreLabel, onQueueMoreClick }) => {
  const job = event.resource;
  const isVisit = job.isVisit || event.eventType === 'visit';
  const techVisit = job.technicianVisit;
  
  const clientName = job.client_name || 'No Client';
  const description = job.description || 'No description';
  const admin = job.admin || 'No Admin';
  const priority = job.priority || 2;
  const priorityStyle = getPriorityStyle(priority);
  
  // Get technician name for visits
  const technicianName = techVisit 
    ? `${techVisit.first_name || ''} ${techVisit.last_name || ''}`.trim() || techVisit.email || 'Technician'
    : null;
  
  const visitTime = techVisit?.scheduled_time
    ? moment(techVisit.scheduled_time, 'HH:mm').format('h:mm A')
    : null;

  if (isVisit) {
    return (
      <VisitEventContent
        job={job}
        technicianName={technicianName}
        visitTime={visitTime}
        clientName={clientName}
        description={description}
        isDarkMode={isDarkMode}
        isTimeGridView={isTimeGridView}
        queueMoreLabel={queueMoreLabel}
        onQueueMoreClick={onQueueMoreClick}
      />
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        padding: '4px 6px',
        height: '100%',
        width: '100%',
        overflow: isTimeGridView ? 'visible' : 'hidden',
      }}
    >
      {/* Header row with client name and priority chip */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 1,
          mb: 0.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
          {job?.is_locked && (
            <Tooltip title="Job locked - read only" arrow placement="top">
              <LockIcon sx={{ fontSize: 12, color: 'inherit', flexShrink: 0 }} />
            </Tooltip>
          )}
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              lineHeight: 1.2,
              color: 'inherit',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
            }}
          >
            {clientName}
          </Typography>
        </Box>
        <Chip
          label={priorityStyle.label}
          sx={{
            backgroundColor: priorityStyle.bgColor,
            color: priorityStyle.textColor,
            fontSize: '0.65rem',
            fontWeight: 500,
            height: '18px',
            minHeight: '18px',
            borderRadius: '3px',
            flexShrink: 0,
            '& .MuiChip-label': {
              padding: '0 6px',
              fontSize: '0.65rem',
            },
          }}
        />
      </Box>
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.7rem',
          lineHeight: 1.2,
          color: 'inherit',
          opacity: 0.9,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          wordBreak: 'break-word',
        }}
      >
        {description}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.65rem',
          lineHeight: 1.2,
          color: 'inherit',
          opacity: 0.8,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          marginTop: 'auto',
        }}
      >
        {admin}
      </Typography>
      {queueMoreLabel && (
        <QueueMoreButton
          label={queueMoreLabel}
          onClick={(e) => {
            e.stopPropagation();
            onQueueMoreClick(e);
          }}
        />
      )}
    </Box>
  );
};

// Constants for localStorage
const CALENDAR_VIEW_STORAGE_KEY = 'calendarViewPreference';
const VALID_VIEWS = ['month', 'week', 'day'];
const DEFAULT_VIEW = 'month';
const EVENT_TYPE_FILTER_STORAGE_KEY = 'calendarEventTypeFilterPreference';
const VALID_EVENT_FILTERS = ['all', 'jobs', 'visits'];
const DEFAULT_EVENT_FILTER = 'all';

// Helper to get initial view from localStorage with validation.
// On mobile, default to Day view (instead of Month) when no preference is stored yet.
const getInitialView = (isMobile) => {
  try {
    const storedView = localStorage.getItem(CALENDAR_VIEW_STORAGE_KEY);
    if (storedView && VALID_VIEWS.includes(storedView)) {
      return storedView;
    }
  } catch (error) {
    console.warn('Failed to read calendar view from localStorage:', error);
  }
  return isMobile ? 'day' : DEFAULT_VIEW;
};

const getInitialEventFilter = () => {
  try {
    const storedFilter = localStorage.getItem(EVENT_TYPE_FILTER_STORAGE_KEY);
    if (storedFilter && VALID_EVENT_FILTERS.includes(storedFilter)) {
      return storedFilter;
    }
  } catch (error) {
    console.warn('Failed to read calendar event filter from localStorage:', error);
  }
  return DEFAULT_EVENT_FILTER;
};

export default function Calendar() {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useIsMobile();
  const { jobs, error } = useSelector(getJobs);
  const user = useSelector(getUser);
  const dispatchers = useSelector(getActiveDispatchers);
  const technicians = useSelector(getTechnicians);
  const [currentDate, setCurrentDate] = React.useState(new Date());
  const [view, setView] = React.useState(() => getInitialView(isMobile));
  const [selectedJob, setSelectedJob] = React.useState(null);
  const [selectedDispatcher, setSelectedDispatcher] = React.useState(null);
  const [selectedTechnician, setSelectedTechnician] = React.useState(null);
  const [selectedEventFilter, setSelectedEventFilter] = React.useState(getInitialEventFilter);
  const [queuePopupState, setQueuePopupState] = React.useState({
    anchorEl: null,
    anchorPosition: null,
    title: '',
    events: [],
  });

  // Filter jobs based on selected dispatcher/technician
  const filteredJobs = React.useMemo(() => {
    if (!jobs || jobs.length === 0) return [];
    
    let filtered = jobs;
    
    // Filter by dispatcher
    if (selectedDispatcher) {
      filtered = filtered.filter(job => {
        // Check if job has dispatcher_id matching selected dispatcher
        if (job.dispatcher_id && job.dispatcher_id === selectedDispatcher.id) {
          return true;
        }
        // Also check dispatcher name/email if available
        if (job.dispatcher && typeof job.dispatcher === 'string') {
          return job.dispatcher.toLowerCase().includes(selectedDispatcher.email?.toLowerCase() || '');
        }
        return false;
      });
    }
    
    // Filter by technician
    if (selectedTechnician) {
      filtered = filtered.filter(job => {
        // Check if job has technicians array
        if (job.technicians && Array.isArray(job.technicians)) {
          return job.technicians.some(tech => 
            tech.technician_id === selectedTechnician.id ||
            (tech.first_name && tech.last_name && 
             `${tech.first_name} ${tech.last_name}`.toLowerCase() === 
             `${selectedTechnician.first_name} ${selectedTechnician.last_name}`.toLowerCase())
          );
        }
        return false;
      });
    }
    
    return filtered;
  }, [jobs, selectedDispatcher, selectedTechnician]);

  // Transform jobs and technician visits into calendar events
  const events = React.useMemo(() => {
    if (!filteredJobs || filteredJobs.length === 0) return [];
    
    const allEvents = [];
    
    filteredJobs.forEach((job) => {
      const jobDate = job.created_at ? new Date(job.created_at) : new Date();
      
      const { backgroundColor, borderColor } = getCalendarEventStatusColors(job.status);
      const jobRange = ensureMinimumEventDuration(
        jobDate,
        jobDate,
        JOB_EVENT_MIN_DURATION_MINUTES
      );

      // Create job event
      const jobEvent = {
        id: `job-${job.id}`,
        title: job.client_name || 'No Title',
        start: jobRange.start,
        end: jobRange.end,
        isPointInTime: jobRange.isPointInTime,
        resource: job,
        eventType: 'job',
        style: {
          backgroundColor,
          borderColor,
          borderWidth: '2px',
          borderStyle: 'solid',
          color: borderColor,
          borderRadius: '4px',
          padding: 0,
          minHeight: '60px',
        },
      };
      
      allEvents.push(jobEvent);
      
      // Create separate events for each technician visit (only if scheduled_date exists)
      if (job.technicians && Array.isArray(job.technicians) && job.technicians.length > 0) {
        job.technicians.forEach((techAssignment) => {
          // Only create visit event if scheduled_date exists
          if (techAssignment.scheduled_date) {
            // Parse date as local time to avoid timezone issues
            let visitDate = parseLocalDate(techAssignment.scheduled_date);
            if (!visitDate) return; // Skip if date parsing failed
            
            // If scheduled_time exists, combine it with the date
            if (techAssignment.scheduled_time) {
              const [hours, minutes] = techAssignment.scheduled_time.split(':');
              visitDate.setHours(parseInt(hours) || 0, parseInt(minutes) || 0, 0, 0);
            }
            
            const visitRange = ensureMinimumEventDuration(
              visitDate,
              visitDate,
              VISIT_EVENT_MIN_DURATION_MINUTES
            );

            const visitEvent = {
              id: `visit-${techAssignment.id || `${job.id}-${techAssignment.technician_id}`}`,
              title: job.client_name || 'No Title',
              start: visitRange.start,
              end: visitRange.end,
              isPointInTime: visitRange.isPointInTime,
              resource: {
                ...job,
                technicianVisit: techAssignment,
                isVisit: true,
              },
              eventType: 'visit',
              statusBorderColor: borderColor,
            };
            
            allEvents.push(visitEvent);
          }
        });
      }
    });
    
    return allEvents;
  }, [filteredJobs]);

  const visibleEvents = React.useMemo(() => {
    if (selectedEventFilter === 'jobs') {
      return events.filter((event) => event.eventType === 'job');
    }
    if (selectedEventFilter === 'visits') {
      return events.filter((event) => event.eventType === 'visit');
    }
    return events;
  }, [events, selectedEventFilter]);

  const queueMetadata = React.useMemo(() => buildQueueMetadata(visibleEvents), [visibleEvents]);

  const closeQueuePopup = React.useCallback(() => {
    setQueuePopupState({
      anchorEl: null,
      anchorPosition: null,
      title: '',
      events: [],
    });
  }, []);

  const openQueuePopupAtClick = React.useCallback((mouseEvent, title, popupEvents) => {
    const rect = mouseEvent.currentTarget.getBoundingClientRect();
    setQueuePopupState({
      anchorEl: null,
      anchorPosition: {
        top: Math.round(rect.bottom),
        left: Math.round(rect.left),
      },
      title,
      events: popupEvents,
    });
  }, []);

  const getEventTimeLabel = React.useCallback((event) => {
    if (!hasExplicitTime(event)) {
      return 'All day';
    }

    const start = moment(event.start);
    const end = moment(event.end);

    if (!start.isValid() || !end.isValid()) return 'Time unavailable';

    if (event.isPointInTime) {
      return start.format('h:mm A');
    }

    const normalizedEnd = end.isAfter(start) ? end : start.clone().add(1, 'minute');
    if (normalizedEnd.diff(start, 'minutes') <= 1) {
      return start.format('h:mm A');
    }

    return `${start.format('h:mm A')} - ${normalizedEnd.format('h:mm A')}`;
  }, []);

  const handleNavigate = (action) => {
    if (action === 'PREV') {
      if (view === 'month') {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
      } else if (view === 'week') {
        setCurrentDate(new Date(currentDate.getTime() - 7 * 24 * 60 * 60 * 1000));
      } else {
        setCurrentDate(new Date(currentDate.getTime() - 24 * 60 * 60 * 1000));
      }
    } else if (action === 'NEXT') {
      if (view === 'month') {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
      } else if (view === 'week') {
        setCurrentDate(new Date(currentDate.getTime() + 7 * 24 * 60 * 60 * 1000));
      } else {
        setCurrentDate(new Date(currentDate.getTime() + 24 * 60 * 60 * 1000));
      }
    } else if (action === 'TODAY') {
      setCurrentDate(new Date());
    }
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null && VALID_VIEWS.includes(newView)) {
      setView(newView);
      try {
        localStorage.setItem(CALENDAR_VIEW_STORAGE_KEY, newView);
      } catch (error) {
        console.warn('Failed to save calendar view to localStorage:', error);
      }
    }
  };

  const handleCalendarViewChange = (nextView) => {
    if (!nextView || !VALID_VIEWS.includes(nextView)) return;
    setView(nextView);
    try {
      localStorage.setItem(CALENDAR_VIEW_STORAGE_KEY, nextView);
    } catch (storageError) {
      console.warn('Failed to save calendar view to localStorage:', storageError);
    }
  };

  const handleEventFilterChange = (event, newFilter) => {
    if (newFilter !== null && VALID_EVENT_FILTERS.includes(newFilter)) {
      setSelectedEventFilter(newFilter);
      try {
        localStorage.setItem(EVENT_TYPE_FILTER_STORAGE_KEY, newFilter);
      } catch (error) {
        console.warn('Failed to save calendar event filter to localStorage:', error);
      }
    }
  };

  const handleSelectEvent = (event) => {
    // For visit events, extract the original job (without visit-specific properties)
    // For job events, use the resource as-is
    if (event.resource.isVisit) {
      // Create a clean job object without visit-specific properties
      const { technicianVisit, isVisit, ...job } = event.resource;
      setSelectedJob(job);
    } else {
      setSelectedJob(event.resource);
    }
  };

  const handleQueueEventClick = React.useCallback((event) => {
    closeQueuePopup();
    handleSelectEvent(event);
  }, [closeQueuePopup]);

  const handleWeekDayQueueClick = React.useCallback((mouseEvent, event) => {
    const clusterInfo = queueMetadata.eventClusterInfo.get(event.id);
    if (!clusterInfo?.isAnchor) return;

    const cluster = queueMetadata.overlapClusters.get(clusterInfo.clusterId);
    if (!cluster) return;

    const displayDate = moment(event.start).format('ddd, MMM D h:mm A');
    openQueuePopupAtClick(
      mouseEvent,
      `Overlapping events - ${displayDate}`,
      cluster.events
    );
  }, [openQueuePopupAtClick, queueMetadata.eventClusterInfo, queueMetadata.overlapClusters]);

  const MonthDateHeader = React.useCallback(({ date, label, drilldownView, onDrillDown }) => {
    const dayKey = getDayKey(date);
    const count = queueMetadata.monthCounts.get(dayKey) || 0;
    const hiddenCount = Math.max(0, count - 2);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
        {drilldownView ? (
          <button type="button" className="rbc-button-link" onClick={onDrillDown}>
            {label}
          </button>
        ) : (
          <span>{label}</span>
        )}
        {hiddenCount > 0 && (
          <Button
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const dayEvents = queueMetadata.eventsByDay.get(dayKey) || [];
              openQueuePopupAtClick(
                e,
                `All events - ${moment(date).format('dddd, MMMM D')}`,
                dayEvents
              );
            }}
            sx={{
              minHeight: '18px',
              px: 0.75,
              py: 0,
              textTransform: 'none',
              lineHeight: 1,
              fontSize: '0.65rem',
              borderRadius: 1,
            }}
          >
            +{hiddenCount} more
          </Button>
        )}
      </Box>
    );
  }, [openQueuePopupAtClick, queueMetadata.eventsByDay, queueMetadata.monthCounts]);

  // Custom event style
  const eventStyleGetter = (event) => {
    const isTimeGridView = view === 'week' || view === 'day';
    const clusterInfo = isTimeGridView
      ? queueMetadata.eventClusterInfo.get(event.id)
      : null;
    const anchorSpacing = clusterInfo?.isAnchor ? { marginBottom: '22px' } : {};

    if (event.eventType === 'visit') {
      return {
        style: {
          ...getVisitEventSurfaceStyle(isDarkMode, event.statusBorderColor || '#6366f1'),
          ...(isTimeGridView ? { minHeight: '108px' } : {}),
          ...anchorSpacing,
        },
      };
    }
    return {
      style: {
        ...event.style,
        ...(isTimeGridView ? { minHeight: '96px' } : {}),
        ...anchorSpacing,
      },
    };
  };

  // Error state
  if (error) {
    return (
      <Paper
        elevation={0}
        sx={{
          height: '100vh',
          width: '100%',
          padding: 3,
          backgroundColor: 'background.default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            color: 'text.primary',
            background: 'background.paper',
            padding: '24px',
            borderRadius: '8px',
            maxWidth: '400px',
            textAlign: 'center',
            fontWeight: 'bold',
            fontSize: '1.2rem',
            boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(211,47,47,0.15)',
          }}
        >
          <span style={{ fontSize: '2rem', display: 'block', marginBottom: '12px' }}>⚠️</span>
          {error}
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          height: '100vh',
          width: '100%',
          padding: { xs: 1, md: 3 },
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: '2rem',
                  color: 'text.primary',
                  mb: 0.5,
                }}
              >
                Calendar
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                }}
              >
                View and manage jobs by date
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Filters */}
        <Box
          sx={{
            display: 'flex',
            gap: { xs: 1, md: 2 },
            mb: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon sx={{ color: 'text.secondary', fontSize: '1.25rem' }} />
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              Filters:
            </Typography>
          </Box>
          
          <Autocomplete
            options={dispatchers || []}
            value={selectedDispatcher}
            onChange={(event, newValue) => {
              setSelectedDispatcher(newValue);
              // Clear technician filter when dispatcher changes
              if (newValue) {
                setSelectedTechnician(null);
              }
            }}
            getOptionLabel={(option) => {
              if (!option) return '';
              if (typeof option === 'string') return option;
              return option.email || `${option.first_name || ''} ${option.last_name || ''}`.trim() || 'Dispatcher';
            }}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            sx={{ minWidth: { xs: '100%', sm: 200, md: 250 } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by Dispatcher"
                size="small"
                sx={{
                  backgroundColor: 'background.paper',
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem',
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Typography variant="body2">
                  {option.email || `${option.first_name || ''} ${option.last_name || ''}`.trim() || 'Dispatcher'}
                </Typography>
              </Box>
            )}
          />
          
          <Autocomplete
            options={(technicians || []).filter(isTechnicianAssignable)}
            value={selectedTechnician}
            onChange={(event, newValue) => {
              setSelectedTechnician(newValue);
              // Clear dispatcher filter when technician changes
              if (newValue) {
                setSelectedDispatcher(null);
              }
            }}
            getOptionLabel={(option) => {
              if (!option) return '';
              if (typeof option === 'string') return option;
              return `${option.first_name || ''} ${option.last_name || ''}`.trim() || option.email || 'Technician';
            }}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            sx={{ minWidth: { xs: '100%', sm: 200, md: 250 } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by Technician"
                size="small"
                sx={{
                  backgroundColor: 'background.paper',
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem',
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Typography variant="body2">
                  {`${option.first_name || ''} ${option.last_name || ''}`.trim() || option.email || 'Technician'}
                </Typography>
              </Box>
            )}
          />

          <ToggleButtonGroup
            value={selectedEventFilter}
            exclusive
            onChange={handleEventFilterChange}
            aria-label="calendar event type filter"
            size="small"
            sx={{
              backgroundColor: 'background.paper',
              border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
              borderRadius: 1,
              '& .MuiToggleButton-root': {
                border: 'none',
                textTransform: 'none',
                fontWeight: 600,
                px: 1.5,
                '&.Mui-selected': {
                  backgroundColor: isDarkMode ? '#fff' : '#000',
                  color: isDarkMode ? '#000' : '#fff',
                  '&:hover': {
                    backgroundColor: isDarkMode ? '#f5f5f5' : '#333',
                  },
                },
              },
            }}
          >
            <ToggleButton value="all" aria-label="show all events">
              All
            </ToggleButton>
            <ToggleButton value="jobs" aria-label="show jobs only">
              Jobs
            </ToggleButton>
            <ToggleButton value="visits" aria-label="show technician visits only">
              Technician Visits
            </ToggleButton>
          </ToggleButtonGroup>
          
          {(selectedDispatcher || selectedTechnician || selectedEventFilter !== DEFAULT_EVENT_FILTER) && (
            <Button
              startIcon={<ClearIcon />}
              onClick={() => {
                setSelectedDispatcher(null);
                setSelectedTechnician(null);
                setSelectedEventFilter(DEFAULT_EVENT_FILTER);
                try {
                  localStorage.setItem(EVENT_TYPE_FILTER_STORAGE_KEY, DEFAULT_EVENT_FILTER);
                } catch (error) {
                  console.warn('Failed to reset calendar event filter in localStorage:', error);
                }
              }}
              sx={{
                textTransform: 'none',
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              Clear Filters
            </Button>
          )}
        </Box>

        {/* Calendar Controls */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexWrap: 'wrap',
            gap: { xs: 1, md: 2 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={() => handleNavigate('PREV')}
              sx={{
                backgroundColor: 'background.paper',
                border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                '&:hover': {
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Button
              onClick={() => handleNavigate('TODAY')}
              startIcon={<TodayIcon />}
              sx={{
                backgroundColor: 'background.paper',
                color: 'text.primary',
                border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              Today
            </Button>
            <IconButton
              onClick={() => handleNavigate('NEXT')}
              sx={{
                backgroundColor: 'background.paper',
                border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                '&:hover': {
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                ml: 2,
                fontWeight: 600,
                color: 'text.primary',
                minWidth: { xs: '120px', md: '200px' },
              }}
            >
              {moment(currentDate).format(view === 'month' ? 'MMMM YYYY' : view === 'week' ? 'MMMM D, YYYY' : 'dddd, MMMM D, YYYY')}
            </Typography>
          </Box>

          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={handleViewChange}
            aria-label="calendar view"
            sx={{
              backgroundColor: 'background.paper',
              border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
              borderRadius: 1,
              '& .MuiToggleButton-root': {
                border: 'none',
                textTransform: 'none',
                fontWeight: 600,
                '&.Mui-selected': {
                  backgroundColor: isDarkMode ? '#fff' : '#000',
                  color: isDarkMode ? '#000' : '#fff',
                  '&:hover': {
                    backgroundColor: isDarkMode ? '#f5f5f5' : '#333',
                  },
                },
              },
            }}
          >
            <ToggleButton value="month" aria-label="month view">
              <ViewModuleIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
              Month
            </ToggleButton>
            <ToggleButton value="week" aria-label="week view">
              <ViewWeekIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
              Week
            </ToggleButton>
            <ToggleButton value="day" aria-label="day view">
              <ViewDayIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
              Day
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Calendar */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: 'background.paper',
            borderRadius: 2,
            border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
            overflow: 'hidden',
            boxShadow: isDarkMode ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.12)',
            '& .rbc-calendar': {
              height: '100%',
            },
            '& .rbc-header': {
              borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
              padding: '12px 8px',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: 'text.secondary',
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : '#fafafa',
            },
            '& .rbc-today': {
              backgroundColor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : '#f0f7ff',
            },
            '& .rbc-off-range-bg': {
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : '#fafafa',
            },
            '& .rbc-day-bg': {
              borderColor: isDarkMode ? '#334155' : '#e0e0e0',
            },
            '& .rbc-date-cell': {
              color: 'text.primary',
            },
            '& .rbc-date-cell > a': {
              color: 'text.primary',
            },
            '& .rbc-off-range-bg .rbc-day-bg': {
              color: 'text.secondary',
            },
            '& .rbc-off-range': {
              color: 'text.secondary',
            },
            '& .rbc-event': {
              padding: 0,
              borderRadius: '4px',
              fontSize: '0.8125rem',
              fontWeight: 500,
              cursor: 'pointer',
              overflow: 'hidden',
              '&:hover': {
                opacity: 0.9,
                transform: 'scale(1.02)',
                transition: 'all 0.2s ease',
              },
            },
            '& .rbc-time-view .rbc-event': {
              minHeight: '108px',
              overflow: 'visible',
            },
            '& .rbc-time-view .rbc-event-content': {
              height: '100%',
              width: '100%',
              overflow: 'visible',
            },
            '& .rbc-time-view .rbc-event-label': {
              display: 'none',
            },
            '& .rbc-event-content': {
              height: '100%',
              width: '100%',
            },
            '& .rbc-event-label': {
              fontSize: '0.75rem',
            },
            '& .rbc-row-segment': {
              maxHeight: '62px',
              overflow: 'hidden',
            },
            '& .rbc-day-slot .rbc-time-slot': {
              borderTop: isDarkMode ? '1px solid #334155' : '1px solid #f5f5f5',
            },
            '& .rbc-time-header-content': {
              borderLeft: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
            },
            '& .rbc-time-content': {
              borderTop: isDarkMode ? '2px solid #334155' : '2px solid #e0e0e0',
              borderColor: isDarkMode ? '#334155' : '#e0e0e0',
            },
            '& .rbc-time-slot': {
              borderTop: isDarkMode ? '1px solid #334155' : '1px solid #f5f5f5',
            },
            '& .rbc-current-time-indicator': {
              backgroundColor: '#1976d2',
            },
            '& .rbc-toolbar': {
              display: 'none',
            },
            '& .rbc-time-view': {
              borderColor: isDarkMode ? '#334155' : '#e0e0e0',
            },
            '& .rbc-time-header': {
              borderColor: isDarkMode ? '#334155' : '#e0e0e0',
            },
            '& .rbc-agenda-view table': {
              borderColor: isDarkMode ? '#334155' : '#e0e0e0',
            },
            '& .rbc-agenda-date-cell, .rbc-agenda-time-cell': {
              borderColor: isDarkMode ? '#334155' : '#e0e0e0',
              color: 'text.primary',
            },
            '& .rbc-agenda-event-cell': {
              borderColor: isDarkMode ? '#334155' : '#e0e0e0',
            },
          }}
        >
          <BigCalendar
            localizer={localizer}
            events={visibleEvents}
            startAccessor="start"
            endAccessor="end"
            dayLayoutAlgorithm="overlap"
            allDayMaxRows={2}
            style={{ height: 'calc(100vh - 280px)' }}
            view={view}
            date={currentDate}
            onNavigate={setCurrentDate}
            onView={handleCalendarViewChange}
            onSelectEvent={handleSelectEvent}
            eventPropGetter={eventStyleGetter}
            components={{
              event: (props) => {
                const isTimeGridView = view === 'week' || view === 'day';
                const clusterInfo = isTimeGridView
                  ? queueMetadata.eventClusterInfo.get(props.event.id)
                  : null;
                const queueMoreLabel = clusterInfo?.isAnchor
                  ? `+${clusterInfo.moreCount} more`
                  : null;

                return (
                  <EventComponent
                    {...props}
                    isDarkMode={isDarkMode}
                    isTimeGridView={isTimeGridView}
                    queueMoreLabel={queueMoreLabel}
                    onQueueMoreClick={(mouseEvent) => handleWeekDayQueueClick(mouseEvent, props.event)}
                  />
                );
              },
              month: {
                dateHeader: MonthDateHeader,
              },
            }}
            popup
            formats={{
              dayFormat: 'ddd',
              weekdayFormat: 'ddd',
              monthHeaderFormat: 'MMMM YYYY',
              dayHeaderFormat: 'dddd, MMMM D',
              dayRangeHeaderFormat: ({ start, end }) =>
                `${moment(start).format('MMM D')} - ${moment(end).format('MMM D, YYYY')}`,
            }}
            messages={{
              next: 'Next',
              previous: 'Previous',
              today: 'Today',
              month: 'Month',
              week: 'Week',
              day: 'Day',
              agenda: 'Agenda',
              date: 'Date',
              time: 'Time',
              event: 'Event',
            }}
          />
        </Box>
      </Paper>

      {/* Job Details Dialog */}
      {selectedJob && (
        <JobDetailsDialog
          selectedJob={selectedJob}
          setSelectedJob={setSelectedJob}
          user={user}
        />
      )}
      <Popover
        open={Boolean(queuePopupState.anchorPosition || queuePopupState.anchorEl)}
        anchorReference={
          queuePopupState.anchorPosition ? 'anchorPosition' : 'anchorEl'
        }
        anchorPosition={queuePopupState.anchorPosition ?? undefined}
        anchorEl={queuePopupState.anchorEl ?? undefined}
        onClose={closeQueuePopup}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disableScrollLock
        PaperProps={{
          sx: {
            minWidth: 320,
            maxWidth: 440,
            maxHeight: 420,
          },
        }}
      >
        <Box sx={{ p: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {queuePopupState.title}
          </Typography>
          <List dense disablePadding>
            {queuePopupState.events.map((event) => (
              <ListItemButton key={event.id} onClick={() => handleQueueEventClick(event)}>
                <ListItemText
                  primary={event.title || 'Untitled'}
                  secondary={getEventTimeLabel(event)}
                  slotProps={{
                    primary: { sx: { fontWeight: 600 } },
                    secondary: { sx: { fontSize: '0.75rem' } },
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Popover>
    </>
  );
}

